import React from 'react';
import { ModernProductData, ProductContentSection, SpecItem, FeatureItem } from '@/types/modernProductContent';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, Star, Copy, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

interface DynamicProductContentProps {
  product: ModernProductData;
}

const DynamicProductContent: React.FC<DynamicProductContentProps> = ({ product }) => {
  const { toast } = useToast();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: "Text copied to clipboard",
      duration: 2000,
    });
  };

  const renderDescriptionSection = (section: ProductContentSection) => (
    <Card key={section.id} className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          {section.title}
        </CardTitle>
        {section.subtitle && (
          <p className="text-muted-foreground">{section.subtitle}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="prose prose-sm max-w-none">
          {section.content?.description ? (
            <p className="text-foreground leading-relaxed">{section.content.description}</p>
          ) : (
            <p className="text-foreground leading-relaxed">{product.description}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderSpecsSection = (section: ProductContentSection) => {
    const specs = section.content?.specs as SpecItem[] || [];
    
    return (
      <Card key={section.id} className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            📋 {section.title}
          </CardTitle>
          {section.subtitle && (
            <p className="text-muted-foreground">{section.subtitle}</p>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {specs.map((spec, index) => (
              <div key={index} className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                <div>
                  <span className="font-medium text-foreground">{spec.key}</span>
                  {spec.featured && <Badge variant="secondary" className="ml-2">Featured</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">
                    {spec.value} {spec.unit && spec.unit}
                  </span>
                  {spec.copyable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(`${spec.key}: ${spec.value}${spec.unit ? ' ' + spec.unit : ''}`)}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderFeaturesSection = (section: ProductContentSection) => {
    const features = section.content?.features as FeatureItem[] || [];
    
    return (
      <Card key={section.id} className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            ⭐ {section.title}
          </CardTitle>
          {section.subtitle && (
            <p className="text-muted-foreground">{section.subtitle}</p>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {features.map((feature, index) => (
              <div key={index} className="p-4 rounded-lg border bg-gradient-to-br from-background to-muted/10">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    {feature.type === 'highlight' && <Star className="h-5 w-5 text-yellow-500" />}
                    {feature.type === 'benefit' && <CheckCircle className="h-5 w-5 text-green-500" />}
                    {feature.type === 'technical' && <FileText className="h-5 w-5 text-blue-500" />}
                    {feature.type === 'usp' && <Badge variant="default" className="text-xs">USP</Badge>}
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{feature.title}</h4>
                    {feature.description && (
                      <p className="text-muted-foreground text-sm">{feature.description}</p>
                    )}
                    {feature.featured && (
                      <Badge variant="outline" className="mt-2">Featured</Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderHeroSection = (section: ProductContentSection) => (
    <Card key={section.id} className="mb-6 bg-gradient-to-br from-primary/5 to-secondary/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          🏆 {section.title}
        </CardTitle>
        {section.subtitle && (
          <p className="text-muted-foreground">{section.subtitle}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="prose prose-sm max-w-none">
          <p className="text-foreground leading-relaxed text-lg">
            {section.content?.description || 'Premium product overview and key selling points.'}
          </p>
          {section.content?.highlights && Array.isArray(section.content.highlights) && (
            <div className="grid gap-2 mt-4">
              {section.content.highlights.map((highlight: string, index: number) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-foreground">{highlight}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderStorySection = (section: ProductContentSection) => (
    <Card key={section.id} className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          📖 {section.title}
        </CardTitle>
        {section.subtitle && (
          <p className="text-muted-foreground">{section.subtitle}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="prose prose-sm max-w-none">
          <p className="text-foreground leading-relaxed">
            {section.content?.story || 'The story behind this amazing product...'}
          </p>
        </div>
      </CardContent>
    </Card>
  );

  const renderGallerySection = (section: ProductContentSection) => (
    <Card key={section.id} className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          🖼️ {section.title}
        </CardTitle>
        {section.subtitle && (
          <p className="text-muted-foreground">{section.subtitle}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-3">
          {section.content?.images && Array.isArray(section.content.images) ? (
            section.content.images.map((image: string, index: number) => (
              <div key={index} className="aspect-square rounded-lg overflow-hidden border">
                <img 
                  src={image} 
                  alt={`Gallery image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))
          ) : (
            <p className="text-muted-foreground col-span-3">No gallery images configured.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderGuaranteesSection = (section: ProductContentSection) => (
    <Card key={section.id} className="mb-6 border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-300">
          🛡️ {section.title}
        </CardTitle>
        {section.subtitle && (
          <p className="text-green-600 dark:text-green-400">{section.subtitle}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {section.content?.guarantees && Array.isArray(section.content.guarantees) ? (
            section.content.guarantees.map((guarantee: any, index: number) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-foreground">{guarantee.title}</h4>
                  <p className="text-muted-foreground text-sm">{guarantee.description}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-muted-foreground">Quality guarantee and warranty information.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderTechnicalSection = (section: ProductContentSection) => (
    <Card key={section.id} className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          🔧 {section.title}
        </CardTitle>
        {section.subtitle && (
          <p className="text-muted-foreground">{section.subtitle}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="prose prose-sm max-w-none">
          <p className="text-foreground leading-relaxed">
            {section.content?.technical || 'Technical specifications and detailed information.'}
          </p>
        </div>
      </CardContent>
    </Card>
  );

  const renderSection = (section: ProductContentSection) => {
    switch (section.section_type) {
      case 'description':
        return renderDescriptionSection(section);
      case 'specs':
        return renderSpecsSection(section);
      case 'features':
        return renderFeaturesSection(section);
      case 'hero':
        return renderHeroSection(section);
      case 'story':
        return renderStorySection(section);
      case 'gallery':
        return renderGallerySection(section);
      case 'guarantees':
        return renderGuaranteesSection(section);
      case 'technical':
        return renderTechnicalSection(section);
      default:
        return (
          <Card key={section.id} className="mb-6">
            <CardHeader>
              <CardTitle>{section.title}</CardTitle>
              {section.subtitle && (
                <p className="text-muted-foreground">{section.subtitle}</p>
              )}
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Content type not supported yet.</p>
            </CardContent>
          </Card>
        );
    }
  };

  // Sort sections by display_order and render them
  const sortedSections = [...product.sections].sort((a, b) => a.display_order - b.display_order);

  // If no sections exist, show basic product description
  if (sortedSections.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Product Description
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="prose prose-sm max-w-none">
            {product.description ? (
              <p className="text-foreground leading-relaxed">{product.description}</p>
            ) : (
              <p className="text-muted-foreground">No product description available.</p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {sortedSections.map(renderSection)}
    </div>
  );
};

export default DynamicProductContent;